import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, CheckCircle2, ChevronDown, Mail } from 'lucide-react';
import { toast } from 'sonner';
import FlowShell from './FlowShell';
import {
  CARD_STYLE, TextField, SelectField, TextareaField, RadioField,
  NextButton, PrimaryButton, formatCoursePrice,
} from './FlowField';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const STEP_LABELS = ['Course', 'About You', 'Your Plan', 'Confirm'];

const EXPERIENCE_OPTIONS = [
  { value: 'beginner',     label: 'Complete Beginner' },
  { value: 'some',         label: 'Some Experience' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced',     label: 'Advanced' },
  { value: 'expert',       label: 'Expert' },
];

const GOAL_OPTIONS = [
  'Get my first tech job',
  'Change careers',
  'Upskill in my current role',
  'Get certified',
  'Build a business',
  'Other',
];

const DELIVERY_OPTIONS = [
  { value: 'face_to_face', label: 'Face to face in London' },
  { value: 'online',       label: 'Online' },
  { value: 'either',       label: 'Either works for me' },
];

const FORMAT_OPTIONS = [
  { value: 'one_to_one',  label: 'One to one' },
  { value: 'small_group', label: 'Small group (2-4 people)' },
  { value: 'either',      label: 'Either works for me' },
];

const TIMING_OPTIONS = [
  { value: 'now',          label: 'Ready to start now' },
  { value: 'within_1m',    label: 'Within 1 month' },
  { value: 'within_3m',    label: 'Within 3 months' },
  { value: 'exploring',    label: 'Just exploring' },
];

const SOURCE_OPTIONS = ['Google', 'LinkedIn', 'Friend or colleague', 'Social media', 'Other'];

const TIER_FEATURES = {
  foundation: [
    'Weekly one to one or small group sessions',
    'Personalised curriculum adjusted to your pace',
    'Real lab environment and hands-on projects',
    'CV and LinkedIn review at programme completion',
    'Direct instructor access between sessions',
    'Certificate of completion',
    '90 day post-completion support',
  ],
  practitioner: [
    'Everything in Foundation plus deeper technical coverage',
    'Industry certification preparation',
    'Portfolio project development',
    'Interview preparation module',
    'Active job search support',
  ],
  advanced: [
    'Everything in Practitioner plus specialist track focus',
    'Advanced certification preparation',
    'Direct mentoring from senior practitioners',
    'Network introductions where relevant',
  ],
  corporate: [
    'Custom programme design for teams',
    'Multiple participant delivery',
    'Bespoke curriculum built around your business needs',
    'Flexible scheduling for working professionals',
    'Account management throughout',
  ],
};

const TIER_LABEL = { foundation: 'Foundation', practitioner: 'Practitioner', advanced: 'Advanced', corporate: 'Corporate' };

function recommendedTier(experience) {
  if (experience === 'beginner') return 'foundation';
  if (experience === 'some') return 'practitioner';
  if (experience === 'intermediate') return 'practitioner';
  if (experience === 'advanced' || experience === 'expert') return 'advanced';
  return 'foundation';
}

function buildMessage(form) {
  const xp = form.experience_level;
  const goal = form.primary_goal;
  let core = '';
  if (xp === 'beginner' && goal === 'Get my first tech job') {
    core = 'You are starting from exactly the right place. Our Foundation track is built for people with no prior experience — no jargon, no assumptions, just clear expert-led sessions that build your skills from the ground up. Most people at your stage land their first tech role within 6 to 9 months of completing this programme. Your sessions can be face to face in London or online — your choice.';
  } else if (xp === 'beginner' && goal === 'Change careers') {
    core = 'Changing careers without prior experience in the new field feels daunting — but it is one of the most common paths through Acodia Learn. Our Foundation track will give you the foundations, the credentials, and the confidence to make the move. We will work around your current commitments.';
  } else if (xp === 'intermediate' && goal === 'Change careers') {
    core = 'You have enough knowledge to move faster than someone starting from scratch. Our Practitioner track will close the specific gaps between where you are and where employers need you to be — without wasting time on things you already know. Career changers at your level typically reach job-ready within 3 to 6 months.';
  } else if ((xp === 'advanced' || xp === 'expert') && goal === 'Upskill in my current role') {
    core = 'You are ready for our Advanced track. Rather than following a fixed curriculum, we will build your programme around your specific objectives and the exact skills your target role requires. Direct access to your instructor throughout.';
  } else if (xp === 'some') {
    core = 'You are not quite a beginner, but you are not yet at working-professional level. Our Practitioner track is the right fit — we will close the specific skill gaps between where you are now and the role you are aiming for, at a pace that matches your background.';
  } else {
    core = 'Based on your answers we have set a recommended tier below. During your consultation we will confirm the exact fit — if your goals suggest a different track we will say so and adjust the plan.';
  }
  if (form.preferred_delivery === 'face_to_face') {
    core += ' You have indicated a preference for face to face sessions. We are based in London and accommodate this — we will confirm your location and schedule your first session during your consultation.';
  }
  return core;
}

export default function LearnFlow({ onExit }) {
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [expandedTier, setExpandedTier] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    course_id: '',
    course_name: '',
    first_name: '',
    last_name: '',
    email: '',
    experience_level: '',
    primary_goal: '',
    preferred_delivery: '',
    preferred_format: '',
    how_soon: '',
    source: '',
    additional_details: '',
    selected_tier: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/courses`);
        if (!res.ok) throw new Error('load failed');
        const body = await res.json();
        if (!cancelled) setCourses(body.courses);
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedCourse = useMemo(
    () => courses.find((c) => c.id === form.course_id),
    [courses, form.course_id],
  );

  const rec = recommendedTier(form.experience_level);

  useEffect(() => {
    // Auto-select recommended tier the first time step 3 is shown
    if (step === 3 && !form.selected_tier) set('selected_tier', rec);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Step 2 validation — email regex client-side + required fields
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const step2Ready = form.first_name && form.last_name && emailValid
    && form.experience_level && form.primary_goal
    && form.preferred_delivery && form.preferred_format
    && form.how_soon && form.source;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email.trim().toLowerCase(),
          service: 'learn',
          source: 'pricing_questionnaire',
          experience_level: form.experience_level,
          primary_goal: form.primary_goal,
          interested_in: form.course_name || 'Not decided — help me decide',
          course_id: form.course_id || null,
          answers: {
            preferred_delivery: form.preferred_delivery,
            preferred_format: form.preferred_format,
            how_soon: form.how_soon,
            selected_tier: form.selected_tier,
            additional_details: form.additional_details,
            source_channel: form.source,
          },
        }),
      });
      if (!res.ok) throw new Error(`submit failed: ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      toast.error('Submission failed', { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const priceFor = (tier) => {
    if (!selectedCourse) return 'Price on consultation';
    if (tier === 'corporate') return selectedCourse.corporate || 'Contact for Quote';
    const v = selectedCourse.prices?.[tier];
    return formatCoursePrice(v);
  };

  // ── Step renderers ─────────────────────────────────────────────
  const renderStep1 = () => (
    <>
      {loadingCourses ? (
        <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading courses…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => {
              const isSelected = c.id === form.course_id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`learn-course-${c.id}`}
                  onClick={() => { set('course_id', c.id); set('course_name', c.name); }}
                  className="text-left p-5 rounded-lg relative transition-all"
                  style={{
                    background: isSelected ? 'var(--a-glow)' : 'var(--l1)',
                    border: `1px solid ${isSelected ? 'var(--a)' : 'var(--b1)'}`,
                  }}
                >
                  {isSelected && (
                    <span
                      className="absolute top-3 right-3 rounded-full flex items-center justify-center"
                      style={{ width: 20, height: 20, background: 'var(--a)', color: 'var(--white)' }}
                    >
                      <Check size={12} />
                    </span>
                  )}
                  <span
                    className="inline-flex font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded mb-3"
                    style={{ background: `var(--track-${c.track}-bg)`, color: `var(--track-${c.track}-fg)` }}
                  >
                    {c.track}
                  </span>
                  <h3 className="font-display text-base font-semibold mb-1" style={{ color: 'var(--white)' }}>
                    {c.name}
                  </h3>
                  <p className="font-mono text-[11px]" style={{ color: 'var(--ts)' }}>{c.duration_weeks} weeks</p>
                  {c.status === 'draft' && (
                    <span
                      className="inline-block mt-2 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded"
                      style={{ background: 'var(--warn-10)', color: 'var(--warn)' }}
                    >
                      Coming soon
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-4">
            <button
              type="button"
              data-testid="learn-course-undecided"
              onClick={() => { set('course_id', ''); set('course_name', 'Not decided — help me decide'); }}
              className="w-full text-left p-4 rounded-lg"
              style={{
                background: form.course_name === 'Not decided — help me decide' ? 'var(--a-glow)' : 'rgba(255,255,255,0.025)',
                border: `1px solid ${form.course_name === 'Not decided — help me decide' ? 'var(--a)' : 'var(--b1)'}`,
              }}
            >
              <span className="font-body text-sm" style={{ color: 'var(--white)' }}>
                I am interested in more than one — help me decide
              </span>
            </button>
          </div>
        </>
      )}
      <NextButton
        disabled={!form.course_id && form.course_name !== 'Not decided — help me decide'}
        onClick={() => setStep(2)}
        testId="learn-step-1-next"
      />
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="First Name" value={form.first_name} onChange={(v) => set('first_name', v)} required testId="learn-first" />
        <TextField label="Last Name"  value={form.last_name}  onChange={(v) => set('last_name', v)}  required testId="learn-last" />
      </div>
      <div className="mt-4">
        <TextField label="Email" type="email" value={form.email} onChange={(v) => set('email', v)} required testId="learn-email" />
        {form.email && !emailValid && (
          <p className="font-body text-[11px] mt-1" style={{ color: 'var(--warn)' }}>Please enter a valid email address.</p>
        )}
      </div>
      <div className="mt-4">
        <SelectField label="Experience Level" value={form.experience_level} onChange={(v) => set('experience_level', v)} options={EXPERIENCE_OPTIONS} required testId="learn-experience" />
      </div>
      <div className="mt-4">
        <SelectField label="Primary Goal" value={form.primary_goal} onChange={(v) => set('primary_goal', v)} options={GOAL_OPTIONS} required testId="learn-goal" />
      </div>
      <div className="mt-4">
        <RadioField label="Preferred delivery" value={form.preferred_delivery} onChange={(v) => set('preferred_delivery', v)} options={DELIVERY_OPTIONS} required testId="learn-delivery" />
      </div>
      <div className="mt-4">
        <RadioField label="Preferred format" value={form.preferred_format} onChange={(v) => set('preferred_format', v)} options={FORMAT_OPTIONS} required testId="learn-format" />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField label="How soon?" value={form.how_soon} onChange={(v) => set('how_soon', v)} options={TIMING_OPTIONS} required testId="learn-timing" />
        <SelectField label="How did you hear about us?" value={form.source} onChange={(v) => set('source', v)} options={SOURCE_OPTIONS} required testId="learn-source" />
      </div>
      <div className="mt-4">
        <TextareaField
          label="Anything else you would like us to know before your consultation?"
          value={form.additional_details}
          onChange={(v) => set('additional_details', v)}
          placeholder="Tell us anything specific about your situation, goals, timeline, or questions you want answered. The more you share the more useful your consultation will be."
          rows={5}
          testId="learn-details"
        />
      </div>
      <NextButton disabled={!step2Ready} onClick={() => setStep(3)} testId="learn-step-2-next" />
    </>
  );

  const renderStep3 = () => {
    const tiers = ['foundation', 'practitioner', 'advanced', 'corporate'];
    const message = buildMessage(form);
    return (
      <>
        {/* Dynamic message */}
        <div
          className="p-5 rounded-lg mb-6"
          style={{ background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
          data-testid="learn-recommendation-message"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            recommendation
          </p>
          <p className="font-body text-sm mt-2" style={{ color: 'var(--white)', lineHeight: '1.72' }}>
            {message}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((t) => {
            const isSelected = form.selected_tier === t;
            const isRecommended = t === rec;
            const isCorporate = t === 'corporate';
            return (
              <div
                key={t}
                data-testid={`learn-tier-${t}`}
                className="p-5 rounded-lg flex flex-col relative"
                style={{
                  background: isSelected ? 'var(--a-glow)' : 'var(--l1)',
                  border: `1px solid ${isSelected ? 'var(--a)' : isRecommended ? 'var(--a-border)' : 'var(--b1)'}`,
                }}
              >
                {isRecommended && !isSelected && (
                  <span
                    className="absolute -top-2 left-4 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded"
                    style={{ background: 'var(--a)', color: 'var(--white)' }}
                  >
                    recommended
                  </span>
                )}
                <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
                  {TIER_LABEL[t]}
                </p>
                <p className="font-mono text-lg font-semibold mt-1 mb-3" style={{ color: 'var(--white)' }}>
                  {priceFor(t)}
                </p>
                <button
                  type="button"
                  onClick={() => setExpandedTier(expandedTier === t ? null : t)}
                  className="flex items-center gap-1 font-body text-xs mb-3"
                  style={{ color: 'var(--a)' }}
                >
                  What this includes <ChevronDown size={12} className="transition-transform" style={{ transform: expandedTier === t ? 'rotate(180deg)' : 'none' }} />
                </button>
                {expandedTier === t && (
                  <ul className="mb-3 space-y-1">
                    {TIER_FEATURES[t].map((f) => (
                      <li key={f} className="flex items-start gap-1.5 font-body text-xs" style={{ color: 'var(--tm)' }}>
                        <Check size={11} style={{ color: 'var(--a)', marginTop: 3, flexShrink: 0 }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {isCorporate ? (
                  <a
                    href="/#contact"
                    className="mt-auto inline-flex items-center justify-center font-body text-xs h-9 rounded-[3px]"
                    style={{ background: 'transparent', color: 'var(--a)', border: '1px solid var(--a-border)' }}
                  >
                    Contact Us →
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => set('selected_tier', t)}
                    className="mt-auto font-body text-xs h-9 rounded-[3px]"
                    style={{
                      background: isSelected ? 'var(--a)' : 'transparent',
                      color: isSelected ? 'var(--white)' : 'var(--tm)',
                      border: `1px solid ${isSelected ? 'var(--a)' : 'var(--b1)'}`,
                    }}
                  >
                    {isSelected ? 'Selected' : 'Select'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <NextButton disabled={!form.selected_tier || form.selected_tier === 'corporate'} onClick={() => setStep(4)} testId="learn-step-3-next" />
      </>
    );
  };

  const renderStep4 = () => {
    if (submitted) {
      return (
        <div className="text-center max-w-xl mx-auto py-6" data-testid="learn-success">
          <div
            className="inline-flex items-center justify-center rounded-full mb-6"
            style={{
              width: 64, height: 64,
              background: 'var(--ok-10)', border: '1px solid var(--ok)',
              boxShadow: '0 0 40px rgba(74,222,128,0.25)',
            }}
          >
            <CheckCircle2 size={32} style={{ color: 'var(--ok)' }} />
          </div>
          <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--white)' }}>
            Your details have been received.
          </h2>
          <p className="font-body text-sm" style={{ color: 'var(--tm)' }}>
            Expect a response within one business day. We will be in touch to confirm your consultation time.
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center gap-1.5 font-body text-xs mt-6"
            style={{ color: 'var(--a)' }}
          >
            Have a question first? Contact us →
          </a>
        </div>
      );
    }
    return (
      <>
        <div className="p-6 rounded-lg mb-6" style={CARD_STYLE}>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--ts)' }}>summary</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 font-body text-sm" style={{ color: 'var(--tm)' }}>
            <div><span style={{ color: 'var(--ts)' }}>Course: </span>{form.course_name || '—'}</div>
            <div><span style={{ color: 'var(--ts)' }}>Tier: </span>{TIER_LABEL[form.selected_tier] || '—'}</div>
            <div><span style={{ color: 'var(--ts)' }}>Delivery: </span>{DELIVERY_OPTIONS.find((o) => o.value === form.preferred_delivery)?.label || '—'}</div>
            <div><span style={{ color: 'var(--ts)' }}>Starting from: </span>
              <span className="font-mono" style={{ color: 'var(--white)' }}>{priceFor(form.selected_tier)}</span>
            </div>
          </div>
        </div>
        <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--white)' }}>
          We are looking forward to speaking with you.
        </h2>
        <p className="font-body text-sm mb-6" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
          Book your free 30 minute consultation below. We will review your answers before the call so you get straight to what matters.
        </p>
        <PrimaryButton onClick={handleSubmit} disabled={submitting} testId="learn-step-4-submit">
          <Mail size={14} className="mr-2" />
          {submitting ? 'Sending…' : 'Book My Free Consultation'}
        </PrimaryButton>
        <p className="mt-4">
          <a href="/#contact" className="font-body text-xs" style={{ color: 'var(--a)' }}>
            Have a question first? Contact us →
          </a>
        </p>
      </>
    );
  };

  return (
    <FlowShell
      kicker="acodia learn"
      title={submitted ? 'Thanks — we will be in touch.' : 'Find your programme.'}
      stepLabels={STEP_LABELS}
      step={step}
      onBack={() => setStep((s) => Math.max(1, s - 1))}
      onExit={onExit}
    >
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </FlowShell>
  );
}
